import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2, type LucideIcon } from "lucide-react";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

/**
 * Meridian's buttons (docs/design/meridian.md, Components). One primary per view: the next step. Danger only for what
 * cannot be undone, and it asks first. Sizes 28, 36, and 44 px; 44 is for touch on public pages.
 */
export const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors duration-(--duration-fast) ease-standard disabled:pointer-events-none aria-busy:pointer-events-none",
  {
    variants: {
      variant: {
        primary:
          "bg-brand-600 text-on-brand hover:bg-brand-700 disabled:bg-muted disabled:text-tone-neutral-dot",
        secondary:
          "border border-border-strong bg-surface text-ink hover:bg-canvas disabled:border-border disabled:text-tone-neutral-dot",
        ghost: "text-ink-body hover:bg-muted disabled:text-tone-neutral-dot",
        danger:
          "bg-danger text-on-brand hover:bg-tone-problem-fg disabled:bg-muted disabled:text-tone-neutral-dot",
      },
      size: {
        sm: "h-7 rounded-(--radius-chip) px-2.5 text-small",
        md: "h-9 rounded-(--radius-control) px-3.5 text-body",
        lg: "h-11 rounded-(--radius-control) px-4.5 text-subheading",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element (for example a link) with button styling. */
  asChild?: boolean;
  /**
   * Working: a spinner before the label, which the caller sets to its verb in progress ("Saving…"). The button
   * cannot be pressed again meanwhile.
   */
  busy?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  busy = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild)
    return (
      <Slot className={classes} {...props}>
        {children}
      </Slot>
    );
  return (
    <button className={classes} disabled={disabled} aria-busy={busy || undefined} {...props}>
      {busy && <Icon icon={Loader2} className="animate-spin" />}
      {children}
    </button>
  );
}

const iconButtonSize = {
  sm: "size-7 rounded-(--radius-chip)",
  md: "size-9 rounded-(--radius-control)",
  lg: "size-11 rounded-(--radius-control)",
} as const;

/** A button that is only an icon; its `label` is what a screen reader says and what the tooltip shows. */
export function IconButton({
  icon,
  label,
  variant = "secondary",
  size = "md",
  className,
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-label"> & {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly variant?: "secondary" | "ghost";
  readonly size?: keyof typeof iconButtonSize;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(buttonVariants({ variant }), "px-0", iconButtonSize[size], className)}
      {...props}
    >
      <Icon icon={icon} size={size === "lg" ? 20 : 16} />
    </button>
  );
}
